import { IContentBase } from '../models/IContentBase';
import { STRATEGY_EXERCISE_TYPE, VIDEO_MOMENT_TYPE, STRENGTH_TYPE, CHAPTER_IDENTIFIER } from '../constants';
import { STEPS } from 'models/exercises/IExercisePlan';

const content: IContentBase = {
  welcomeScreenVideoUrl: 'videos/welcomeScreen/afl/background.mp4',
  finaleBackgroundUrl: 'images/dashboard/afl/finale.webp',
  videoOverlayBackgroundUrl: 'images/dashboard/afl/overlay.webp',
  chapters: [
    {
      chapterIdentifier: CHAPTER_IDENTIFIER.KeepingYourCool,
      ordinal: 1,
      activityTextOrdinal: "one",
      title: '<b>Breathing</b><br />exercise',
      strategy: 'Keeping your<br/><b>cool</b>.',
      entryText: "<p>Controlled breathing is a quick hack to calm our body in tense moments.</p><p>Find a quiet place and take a few moments to complete this exercise.</p><p>It will take less than a minute.</p><p><b>Remember to focus on your breathing.</b></p>",
      introContent: '<b>Remember to focus on your breathing.</b> <br/><br/>Controlled breathing is a quick hack to calm our body in tense moments. <br/>Find a quiet place and take a few moments to complete this exercise. <br/>It will take less than a minute.',
      outroContent: 'It’s easy to get fired up, or anxious in high-pressure moments. But pausing for a moment to take a few breaths can help keep us cool, calm, and in control. <br><br>Remember:<br><ul><li>Breathe in for 4 seconds</li><li>Hold for 4 seconds</li><li>Breathe out for 4 seconds</li></ul>',
      dashboardImage: 'images/dashboard/afl/chapter-5.webp',
      icon: 'images/icons/strategy/afl/breathe',
      time: '1 min',
      exerciseType: STRATEGY_EXERCISE_TYPE.Breathe,
      exercise: {
        seconds: null,
        type: VIDEO_MOMENT_TYPE.StrategyExercise,
        shouldPause: true,
      },
    },
    {
      chapterIdentifier: CHAPTER_IDENTIFIER.ManagingYourThoughts,
      ordinal: 2,
      activityTextOrdinal: "two",
      title: 'Choose your<br/><b>thoughts</b>',
      strategy: 'Choose your thoughts',
      entryText: "<p>Understand how your brain can work against you. Once you can spot your thoughts you can better choose which thoughts to listen to!</p>",
      introContent: 'Understand how your brain<br />can work against you.',
      outroContent: "<ul><li>In challenging moments, we can have lots of different thoughts. </li> <li>Some are helpful others might be unhelpful. </li> <li>Remember, don't beat yourself up with unhelpful thoughts.</li><li>Choose helpful thoughts as they can help how we feel. </li> <li>Remember to lean on your team for support.</li></ul>",
      dashboardImage: 'images/dashboard/afl/chapter-1.webp',
      icon: 'images/icons/strategy/afl/thoughts',
      time: '5 min',
      exerciseType: STRATEGY_EXERCISE_TYPE.Choose,
      exercise: {
        cardsHeading: 'Is this a <b>helpful</b> or <b>unhelpful</b> thought?',
        cards: [
          {
            questionText: 'I’ve done this before. I’ve got this.',
            isCorrect: true,
            hint: 'This is a reassuring and helpful thought. It’s a good example of backing yourself.',
          },
          {
            questionText: 'I’m a total loser',
            isCorrect: false,
            hint: 'That doesn’t sound like a very helpful or confident thought. How would it feel if someone said this to you?',
          },
          {
            questionText: 'I’m an idiot',
            isCorrect: false,
            hint: 'Nope not a helpful thought. You wouldn’t say this to a mate who was feeling down on themselves.',
          },
          {
            questionText: 'I’ll be alright. No big deal',
            isCorrect: true,
            hint: 'It’s a good start in helpful thinking.',
          },
          {
            questionText: 'I’ve got a long way to go, but whatever',
            isCorrect: false,
            hint: 'Hmmm. Probably not a very helpful thought.',
          },
          {
            questionText: 'Time for a sausage roll break',
            isCorrect: false,
            hint: 'Good idea. But not super helpful maybe?',
          },
          {
            questionText: 'I’ll bounce back',
            isCorrect: true,
            hint: 'Telling yourself that you’ll bounce back is a resilient and helpful thought.',
          },
          {
            questionText: 'I totally suck!',
            isCorrect: false,
            hint: 'You wouldn’t say it to a mate to help them out. It’s not helpful, motivating or kind.',
          },
          {
            questionText: 'I’m doing my best',
            isCorrect: true,
            hint: 'I’m doing my best is a good start in helpful thinking and shows some self-kindness.',
          },
          {
            questionText: 'I should practice my kicking',
            isCorrect: true,
            hint: 'This is actually helpful. It involves taking action and focusing on what you should do.',
          },
          {
            questionText: 'I need to stop missing so many goals',
            isCorrect: false,
            hint: 'For helpful thoughts you want to focus on what you should do, rather than things you shouldn’t be doing.',
          },
          {
            questionText: 'I’m injured so no point going to training',
            isCorrect: false,
            hint: 'Not necessarily. There might be some things you can still work on safely. So might not be a helpful thought?',
          },
          {
            questionText: 'I’m good at this',
            isCorrect: true,
            hint: 'This is optimistic and confident. It’s a helpful thought.',
          },
        ],
      },
    },
    {
      chapterIdentifier: CHAPTER_IDENTIFIER.ProblemSolving,
      ordinal: 3,
      activityTextOrdinal: "three",
      title: 'Making a<br/><b>plan</b>',
      strategy: 'Make a plan',
      entryText: "<p>Tough problems can be broken down into simple steps, making the way out seem much clearer.</p><p>Note: it helps to do a breathing exercise before doing this step!</p>",
      introContent: 'Use simple steps to help<br />you problem solve tricky situations.',
      outroContent: '<ul><li>When faced with a challenge, sometimes we can fear the worst.</li><li>You could take a walk to clear your head.</li><li>Think about the problem and brainstorm lots of solutions.</li><li>Weigh up pros and cons.</li><li>Chose an option, plan the steps and give it a go.</li><li>If Plan A isn\'t working, there are lots of different options, give Plan B a go.</li><li>Remember to lean on your team when faced with a challenge.</li></ul>',
      dashboardImage: 'images/dashboard/afl/chapter-2.webp',
      icon: 'images/icons/strategy/afl/plan',
      time: '10 min',
      exerciseType: STRATEGY_EXERCISE_TYPE.Plan,
      exercise: {
        steps: [
          {
            step: STEPS.STEP_ONE,
            heading: 'Define the challenge',
            buttonText: 'Next Step',
            description: 'Try and keep it simple, boiling it down to the main issue. If there are multiple challenges, do this task again',
          },
          {
            step: STEPS.STEP_TWO,
            heading: 'Brainstorm solutions',
            buttonText: 'Next Step',
            description: 'Try and come up with as many solutions as possible. Don’t worry how possible or realistic they are at this stage.',
          },
          {
            step: STEPS.STEP_TWO_B,
            heading: 'Select two options',
            buttonText: 'Next Step',
            description: 'Select two solutions you think are worth exploring further.',
          },
          {
            step: STEPS.STEP_THREE,
            heading: 'What are the pros/cons of option',
            buttonText: 'Next Step',
            description: 'Keep each note short and simple',
          },
          {
            step: STEPS.STEP_FOUR,
            heading: 'Which one is your best option, at this stage?',
            buttonText: 'Select Plan',
          },
          {
            step: STEPS.STEP_FIVE,
            heading: 'Plan the steps',
            buttonText: 'Next Step',
            description: 'Break down the plan into simple steps that will help you get closer to your solution.',
          },
          {
            step: STEPS.STEP_SIX,
            heading: 'If it\'s not working...',
            buttonText: 'Finish',
            description: 'Simply go back and re-look at the solutions available to you and go back through this process!',
          },
          {
            step: STEPS.FINAL,
            heading: 'My plan',
            buttonText: 'Continue',
            description: 'Review the steps in your plan and get ready to take action!',
          },
        ],
      },
    },
    {
      chapterIdentifier: CHAPTER_IDENTIFIER.PlayingToYourStrengths,
      ordinal: 4,
      activityTextOrdinal: "four",
      title: 'What are my<br/><b>strengths?</b>',
      strategy: 'Play to your strengths',
      entryText: "<p>Instead of focussing on something you aren’t doing well, work out your strengths and focus on them instead.</p>",
      introContent: 'Determine your own<br />strengths and focus on<br />them.',
      outroContent: '<ul><li>Think about your strengths ON and OFF the field and how to play to these strengths.</li><li>Use your strengths more</li><li>Improve through training</li><li>Help others develop their strengths</li><li>Your team (ON and OFF the field) can help you understand your strengths.</li><li>Remember to remind your team of their strengths.</li></ul>',
      dashboardImage: 'images/dashboard/afl/chapter-3.webp',
      icon: 'images/icons/strategy/afl/strength',
      time: '3 min',
      exerciseType: STRATEGY_EXERCISE_TYPE.Strengths,
      exercise: {
        promptSets: [{
          emptyText: 'Explore different ways to improve this strength.',
          prompts: [{
            strengthCardsType: STRENGTH_TYPE.OnField,
            instruction: 'Select one of your <b>ON-field</b> strengths.',
            instructionCustom: 'Name your <b>on-field</b> strength',
            questions: [{
              text: 'Can you <b>use</b> this strength more?',
              answeredText: 'I can look for opportunities to use this strength more',
            }, {
              text: 'Can you <b>improve</b> on this strength through training?',
              answeredText: 'I can work on improving it',
            }, {
              text: 'Can you <b>help others</b> develop this strength?',
              answeredText: 'I can share it with others',
            }],
          },
          {
            strengthCardsType: STRENGTH_TYPE.OffField,
            instruction: 'Select one of your <b>OFF-field</b> strengths.',
            instructionCustom: 'Name your <b>off-field</b> strength',
            emptyText: 'Explore different ways to improve this strength.',
            questions: [{
              text: 'Can you <b>use</b> this strength more?',
              answeredText: 'I can look for opportunities to use this strength more',
            }, {
              text: 'Can you <b>help others</b> with this strength?',
              answeredText: 'I can use it to help others',
            }, {
              text: 'Can you use this strength in your <b>sport</b>?',
              answeredText: 'I can use it in my sport',
            }],
          }],
        }],
      },
    },
    {
      chapterIdentifier: CHAPTER_IDENTIFIER.ControllingTheControllables,
      ordinal: 5,
      activityTextOrdinal: "five",
      title: 'Taking<br/><b>control</b>',
      introTitle: 'Control the controll&shy;ables',
      strategy: 'Control the controll&shy;ables',
      entryText: "<p>When our brains focus on things we can’t control, it can hold us back. Instead of feeling out of control, focus on things you can control.</p>",
      introContent: 'Some things we can’t<br />control. Let’s focus on what<br />we can.',
      outroContent: "<ul><li>When in a challenging situation there are things that you CAN control and things you can't control.</li><li>Look at what you CAN control.</li><li>Focus on acting on things you CAN control.</li><li>Also focus on appreciating your support team (ON and OFF the field).</li></ul>",
      dashboardImage: 'images/dashboard/afl/chapter-4.webp',
      icon: 'images/icons/strategy/afl/control',
      time: '3 min',
      exerciseType: STRATEGY_EXERCISE_TYPE.Control,
      exercise: {
        numberOfCardsToChoose: 4,
        controlCards: [{
          text: 'The umpire',
          icon: 'images/icons/control/afl/umpire',
          isControllable: false,
          controlFeedback: 'The umpire is not within your control. No matter how much you kick and scream, you can’t change the umpire’s decision. Behaving in that way to an umpire isn’t part of our game.',
          isImmediatelyControllable: false,
        }, {
          text: 'Your opponents',
          icon: 'images/icons/control/afl/opponents',
          isControllable: false,
          controlFeedback: 'You have no control over your opponents.',
          isImmediatelyControllable: false,
        }, {
          text: 'Your teammates',
          icon: 'images/icons/control/afl/teammates',
          isControllable: false,
          controlFeedback: 'The only player in your team you have control over is yourself.',
          isImmediatelyControllable: false,
        }, {
          text: 'The weather',
          icon: 'images/icons/control/afl/weather',
          isControllable: false,
          controlFeedback: 'That would be handy but no you have no control over the weather.',
          isImmediatelyControllable: false,
        }, {
          text: 'Your performance',
          icon: 'images/icons/control/afl/performance',
          isControllable: false,
          controlFeedback: "You can't control your overall performance. But you might be able to control parts of it like your pre-kick routine.",
          isImmediatelyControllable: false,
        }, {
          text: 'Your language',
          icon: 'images/icons/control/afl/language',
          isControllable: true,
          controlFeedback: 'Your language is within your control. You need to focus on things you can control.',
          isImmediatelyControllable: true,
          immediateControlFeedback: 'That’s a great place to start. It’s something you can control straight away.',
        }, {
          text: 'The coach',
          icon: 'images/icons/control/afl/coach',
          isControllable: false,
          controlFeedback: 'Nope. The coach is not something within your control.',
          isImmediatelyControllable: false,
        }, {
          text: 'Your effort',
          icon: 'images/icons/control/afl/effort',
          isControllable: true,
          controlFeedback: 'You got it. Your effort is something you can control.',
          isImmediatelyControllable: true,
          immediateControlFeedback: 'It’s easy to let it affect your play when you’re upset. But you can re-take control by focussing on your effort in the moment.',
        }, {
          text: 'Your attitude',
          icon: 'images/icons/control/afl/reactions',
          isControllable: true,
          controlFeedback: 'Your attitude is within your control, even if it feels tough sometimes.',
          isImmediatelyControllable: true,
          immediateControlFeedback: 'It might seem tough but this is something you can start trying to control right away.',
        }, {
          text: 'The ground conditions',
          icon: 'images/icons/control/afl/pitch',
          isControllable: false,
          controlFeedback: "That's out of your control.",
          isImmediatelyControllable: false,
        }, {
          text: 'Your fitness',
          icon: 'images/icons/control/afl/fitness',
          isControllable: true,
          controlFeedback: 'Your fitness is something you can work on and have some control over.',
          isImmediatelyControllable: false,
          immediateControlFeedback: 'Your fitness is not something you can fix right away. Another option might be more important in this moment.',
        }, {
          text: 'The crowd',
          icon: 'images/icons/control/afl/crowd',
          isControllable: false,
          controlFeedback: 'Nope. Not something you can control.',
          isImmediatelyControllable: false,
        }],
      },
    }, {
      chapterIdentifier: CHAPTER_IDENTIFIER.LeanOnTeammates,
      ordinal: 6,
      activityTextOrdinal: 'six',
      title: 'Lean on your<br/><b>team</b>',
      strategy: 'Lean on your team',
      entryText: "<p>You don’t have to deal with tough times on your own. You have a support team you can lean on.</p><div class=\"row\"><p>Lean on your team for support, and look out for your teammates. Follow the ALEC steps: <b>Ask, Listen, Encourage Action, Check in</b>.</p><img src=\"/images/icons/teammates/teammates.png\" /></div><div class=\"row\"><img src=\"/images/icons/teammates/coach.png\" /><p>Coaches are part of your support team. Lean on them when needed.</p></div><div class=\"row\"><p>Parents are an important part of your support team. Reach out to them when needed.</p><img src=\"/images/icons/teammates/parents.png\" /></div><div class=\"row\"><img src=\"/images/icons/teammates/friends.png\" /><p>Friends are people you can talk to about anything.</p></div><p>Remember to show gratitude for your support team!</p><ul><li>appreciate your team</li><li>support them in return</li><li>recognise the strengths in others</li></ul>",
      outroContent: "",
      introContent: "",
      time: '1 min',
      dashboardImage: 'images/dashboard/afl/chapter-6.webp',
      icon: 'images/icons/strategy/afl/team',
    },
  ],
  strengthCards: {
    [STRENGTH_TYPE.OnField]: [
      {
        text: 'Kicking',
        slug: 'kicking',
        icon: 'images/icons/strength/afl/on-field/kicking',
      }, {
        text: 'Running',
        slug: 'running',
        icon: 'images/icons/strength/afl/on-field/running',
      }, {
        text: 'Marking',
        slug: 'marking',
        icon: 'images/icons/strength/afl/on-field/marking',
      }, {
        text: 'Tackling',
        slug: 'tackling',
        icon: 'images/icons/strength/afl/on-field/tackling',
      }, {
        text: 'Fair play',
        slug: 'fair-play',
        icon: 'images/icons/strength/afl/on-field/fair-play',
      }, {
        text: 'Leadership',
        slug: 'leadership',
        icon: 'images/icons/strength/afl/on-field/leadership',
      }, {
        text: 'Team work',
        slug: 'team-work',
        icon: 'images/icons/strength/afl/on-field/team-work',
      }, {
        text: 'Decision making',
        slug: 'decision-making',
        icon: 'images/icons/strength/afl/on-field/decision-making',
      },
    ],
    [STRENGTH_TYPE.OffField]: [
      {
        text: 'Sense of humour',
        slug: 'sense-of-humour',
        icon: 'images/icons/strength/afl/off-field/humour',
      }, {
        text: 'Adaptable',
        slug: 'adaptable',
        icon: 'images/icons/strength/afl/off-field/adaptable',
      }, {
        text: 'Kind',
        slug: 'kind',
        icon: 'images/icons/strength/afl/off-field/kind',
      }, {
        text: 'Motivated',
        slug: 'motivated',
        icon: 'images/icons/strength/afl/off-field/motivated',
      }, {
        text: 'Creative',
        slug: 'creative',
        icon: 'images/icons/strength/afl/off-field/creative',
      }, {
        text: 'Determined',
        slug: 'determined',
        icon: 'images/icons/strength/afl/off-field/determined',
      }, {
        text: 'Dedicated',
        slug: 'dedicated',
        icon: 'images/icons/strength/afl/off-field/dedicated',
      }, {
        text: 'Hard working',
        slug: 'hard-working',
        icon: 'images/icons/strength/afl/off-field/patient',
      }, {
        text: 'Trustworthy',
        slug: 'trustworthy',
        icon: 'images/icons/strength/afl/off-field/trustworthy',
      }, {
        text: 'Friendly',
        slug: 'friendly',
        icon: 'images/icons/strength/afl/off-field/friendly',
      },
    ],
  },
  urgentHelpBlock: {
    title: '<b>Looking for<br />urgent help?</br>',
    description: 'You are not alone.<br />The following services are here for you.',
    to: '/support-services',
    imgUrl: 'images/dashboard/urgent-help',
  },
  chatMateBlock: {
    title: '<b>Starting a <br />conversation</br>',
    description: 'Learn a few tricks to starting a tough conversation with a mate.',
    to: '/chat-mate',
    imgUrl: 'images/dashboard/chat-mate',
  },
};

export default content;